/**
 * @fileoverview Reactivate User helpers.
 * @description Pure helpers for the Reactivate dialog: narrowing the assignable roles to the Sales
 * ones the admin may bring a user back as, picking the role the form starts on, and building the
 * request body from the form.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { SALES_ROLE_CODES } from '@/shared/constants/sales-role-codes.constants'

import type { ReactivateUserFormValues } from '@/features/users/schemas/reactivate-user-form.schema'
import type {
  ReactivateUserRequest,
  ReactivationRoleOption,
  UserDetail,
} from '@/features/users/types/users.types'
import type { AssignableRole } from '@/shared/types/directory-options.types'

/**
 * @description Narrows the roles an admin may assign to the Sales ones, as plain role choices.
 * @param {AssignableRole[]} roles - Every assignable role.
 * @returns {ReactivationRoleOption[]} The Sales roles.
 */
export function selectSalesRoles(roles: AssignableRole[]): ReactivationRoleOption[] {
  return roles
    .filter((role) => SALES_ROLE_CODES.has(role.roleCode))
    .map(({ roleId, roleCode, name }) => ({ roleId, roleCode, name }))
}

/**
 * @description The role the form starts on: the one the user had, when the admin may still bring
 * someone back as it, or the only one the admin may choose.
 * @param {UserDetail} user - The user coming back.
 * @param {ReactivationRoleOption[]} roles - The roles the admin may choose.
 * @returns {string} The role id, or '' when the admin must choose.
 */
export function initialRoleId(user: UserDetail, roles: ReactivationRoleOption[]): string {
  const previous = roles.find((role) => role.roleId === user.primaryRole?.roleId)
  return previous?.roleId ?? (roles.length === 1 ? (roles[0]?.roleId ?? '') : '')
}

/**
 * @description Builds the Reactivate User body from the form: the note is left out when blank.
 * @param {ReactivateUserFormValues} values - The validated values.
 * @returns {ReactivateUserRequest} The request body.
 */
export function toReactivateUserRequest(values: ReactivateUserFormValues): ReactivateUserRequest {
  return {
    roleId: values.roleId,
    reportingManagerUserId: values.reportingManagerUserId,
    reasonCode: values.reasonCode,
    ...(values.reasonNote !== '' && { reasonNote: values.reasonNote }),
  }
}
